import type { RouteRecordRaw } from "vue-router";
import { useQueryClient } from "@tanstack/vue-query";
import { queries } from "ls/state/queries";
import { useRouter } from "vue-router";
import { routeNames } from "../../router/constants";
import { useOnPremSetupContext } from "./context";

async function getConfigSafely() {
  const queryClient = useQueryClient();
  try {
    return await queryClient.ensureQueryData(queries.config());
  } catch {
    return undefined;
  }
}

export const onPremSetupRoute: RouteRecordRaw = {
  path: "/setup",
  name: routeNames.onPremSetup,
  component: () => import("ls/features/onprem-setup/OnPremSetup.vue"),
  meta: {
    requiresAuth: false,
    onPremSetup: true,
  },
  async beforeEnter(to) {
    const { connection } = useOnPremSetupContext();

    const config = await getConfigSafely();
    if (!config) {
      return true;
    }

    if (config.NeedsDbUpgrade) {
      if (to.name !== routeNames.onPremSetupDatabaseUpgrade) {
        return {
          name: routeNames.onPremSetupDatabaseUpgrade,
        };
      }
    } else if (!connection.value && to.name !== routeNames.onPremSetupDatabaseConnection) {
      return {
        name: routeNames.onPremSetupDatabaseConnection,
      };
    }

    return true;
  },
  children: [
    {
      path: "database-connection",
      name: routeNames.onPremSetupDatabaseConnection,
      component: () => import("ls/features/onprem-setup/DatabaseConnection.vue"),
    },
    {
      path: "database-connection-error",
      name: routeNames.onPremSetupDatabaseConnectionError,
      component: () => import("ls/features/onprem-setup/DatabaseConnectionError.vue"),
    },
    {
      path: "database-upgrade",
      name: routeNames.onPremSetupDatabaseUpgrade,
      component: () => import("ls/features/onprem-setup/DatabaseUpgrade.vue"),
    },
    {
      path: "database-setup",
      name: routeNames.onPremSetupDatabaseSetup,
      component: () => import("ls/features/onprem-setup/DatabaseSetup.vue"),
    },
    {
      path: "database-progress",
      name: routeNames.onPremSetupDatabaseProgress,
      component: () => import("ls/features/onprem-setup/DatabaseProgress.vue"),
    },
    {
      path: "database-user",
      name: routeNames.onPremSetupDatabaseUser,
      component: () => import("ls/features/onprem-setup/DatabaseUser.vue"),
    },
    {
      path: "administrator-user",
      name: routeNames.onPremSetupAdminUser,
      component: () => import("ls/features/onprem-setup/OnPremAdminUser.vue"),
    },
    {
      path: "setup-license",
      name: routeNames.onPremSetupLicense,
      component: () => import("ls/features/onprem-setup/OnPremSetupLicense.vue"),
    },
  ],
};

function useOnPremSetupGuard() {
  const router = useRouter();

  const stop = router.beforeEach(async to => {
    const config = await getConfigSafely();
    if (!config) {
      stop();
      return true;
    }

    if (!config.AxShareOnPrem) {
      stop();
      return true;
    }

    const setupOnly = config.NeedsSetup || config.NeedsDbUpgrade;
    if (!setupOnly) {
      stop();
      return true;
    }

    return to.meta.onPremSetup || { name: routeNames.onPremSetup };
  });
}

function useConfigureOnPremSetupRoutes() {
  const router = useRouter();

  const stop = router.beforeEach(async to => {
    const config = await getConfigSafely();
    if (!config) {
      stop();
      return true;
    }

    if (!config.AxShareOnPrem) {
      stop();
      return true;
    }

    if (config.NeedsSetup || config.NeedsDbUpgrade) {
      if (!router.hasRoute(routeNames.onPremSetup)) {
        router.addRoute(onPremSetupRoute);
        stop();
        return to.fullPath;
      }
    }

    stop();
    return true;
  });
}

export function useConfigureOnPremRoutes() {
  useConfigureOnPremSetupRoutes();
  useOnPremSetupGuard();
}
